import styled from 'styled-components';
import React, {useEffect, useRef, useState} from 'react';
import { gsap, useGSAP } from '@repo/utils/gsap';
import {useIsomorphicLayoutEffect, useIsMobile} from '@repo/ui';
import {COLORS, THEME_MODES} from '@repo/ui/themeSettings';
import { SocialsWrapper, StyledAddress, StyledHeader, StyledSingleSocialItem, StyledText } from '@/components/Menu/shared/BottomRow';
import { MenuArrow } from '@repo/ui/SVGR';

const StyledWrapper = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;
	bottom: 0;
	left: 0;
	height: 90px;
	width: 100%;
	background: ${({theme}) => theme.colors.lightGray};
	padding-top: 38px;
	z-index: 12;
	transform: translateX(-100%);

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		display: none;
	}
`;

const StyledButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 15px;

	svg {
		transform: rotate(180deg);
	}
`;

const StyledButton = styled.div`
	color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
	font-size: 16px;
	padding-left: 15px;
	text-transform: uppercase;

	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding-left: 30px;
	}
`;

const StyledContent = styled.div`
	justify-content: center;
	height: 100%;
	display: flex;
	opacity: 0;
	text-align: right;
	flex-direction: column;
	gap: 55px;
	padding: 0 15px;
	color: ${({ theme }) => (theme.mode === THEME_MODES.LIGHT ? theme.colors.white : theme.colors.twilightBlue)};
	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		padding: 0 30px;
		gap: 40px;
	}
`;

const StyledSingleContactInfoItem = styled.div`
	display: flex;
	justify-content: space-between;
`;

const StyledContactInfoContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	@media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
		flex-direction: row;
		gap: 30px;
	}
`;

const ExpandableBlock = ({ data, globals }) => {
	const buttonText = data?.mobileBottomBarText || '';
	const isMobile = useIsMobile();

	const [isExpanded, setIsExpanded] = useState(false);

	const wrapperRef = useRef(null);
	const tl = useRef(null);

	const contactHeader = data?.contactHeader || '';
	const contactEmail = globals?.email || '';
	const contactPhoneNumber = globals?.phone || '';

	const socialsHeader = data?.socialsHeader || '';
	const socialsItems = globals?.socials || [];

	useGSAP(
		() => {
			tl.current = gsap.timeline({ paused: true });

			tl.current.to(wrapperRef.current, {
				height: isMobile ? '44dvh' : '26dvh',
				background: COLORS.red,
			});
			tl.current.to('.menu-expandable-block-content', {
				autoAlpha: 1
			}, 0)
			tl.current.to('.menu-expand-button', { color: COLORS.white }, '0');
			tl.current.to('.menu-expand-button-icon', { rotate: '360' }, '0');
			tl.current.to('.animatedPath', { fill: COLORS.white }, '0');
		},
		{ scope: wrapperRef, dependencies: [isMobile] },
	);

	useIsomorphicLayoutEffect(() => {
		isExpanded ? tl.current.play() : tl.current.reverse();
	}, [isExpanded]);

	useEffect(() => {
		function handleMouseDown(event) {
			if (wrapperRef && !wrapperRef.current.contains(event.target)) {
				setIsExpanded(false);
			}
		}

		document.addEventListener('mousedown', handleMouseDown);

		return () => {
			document.removeEventListener('mousedown', handleMouseDown);
		};
	}, [wrapperRef, setIsExpanded]);

	return (
		<StyledWrapper ref={wrapperRef} onClick={() => setIsExpanded((prev) => !prev)} className='menu-expandable-block-wrapper'>
			<StyledButtonWrapper>
				<StyledButton className="menu-expand-button">{buttonText}</StyledButton>
				<MenuArrow className="menu-expand-button-icon" />
			</StyledButtonWrapper>
			<StyledContent className='menu-expandable-block-content'>
				<StyledSingleContactInfoItem>
					<StyledHeader>{contactHeader}</StyledHeader>
					<StyledContactInfoContentWrapper>
						<StyledText>{contactEmail}</StyledText>
						<StyledText className='gap-top'>{contactPhoneNumber}</StyledText>
					</StyledContactInfoContentWrapper>
				</StyledSingleContactInfoItem>
				<StyledSingleContactInfoItem>
					<StyledHeader>{socialsHeader}</StyledHeader>
					<SocialsWrapper $colsNumber={Math.round(socialsItems.length / 2)}>
						{socialsItems.map((item) => (
							<StyledSingleSocialItem href={item?.link || ''} target="_blank" key={item?.id}>
								{item.title}
							</StyledSingleSocialItem>
						))}
					</SocialsWrapper>
				</StyledSingleContactInfoItem>
			</StyledContent>
		</StyledWrapper>
	);
};

export default ExpandableBlock;